import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { createShareLink, getUserShareLinks, deleteShareLink, getShareLinkInfo } from '../../services/api'
import { useDataStore } from '../../store/useDataStore'

interface ShareDialogProps {
  projectId?: string  // 可选：用于创建页面
  shareId?: string    // 可选：用于分享管理页面，通过shareId编辑
  projectUuid?: string // 可选：用于分享管理页面，直接提供projectUuid
  onClose: () => void
}

const ShareDialog = ({ projectId, shareId, projectUuid: providedProjectUuid, onClose }: ShareDialogProps) => {
  useTranslation() // Keep hook call for potential future use
  const { data, userUuid, pushProjectToServer } = useDataStore()
  const [isLoading, setIsLoading] = useState(false)
  const [shareLinks, setShareLinks] = useState<Array<{
    shareId: string
    projectUuid: string
    createdAt: string
    expiresAt: string | null
    allowDownload?: boolean
    message?: string | null
    hasPassword?: boolean
    lockInterface?: boolean
    shareUrl: string
  }>>([])
  const [error, setError] = useState<string | null>(null)
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null)
  const [resolvedProjectUuid, setResolvedProjectUuid] = useState<string | null>(null)
  
  // 分享设置状态
  const [allowDownload, setAllowDownload] = useState(true)
  const [lockInterface, setLockInterface] = useState(true)  // 锁定界面设置，默认选中
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('')
  const [message, setMessage] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // 解析 projectUuid：优先使用 providedProjectUuid，其次通过 shareId 获取，最后通过 projectId 获取
  useEffect(() => {
    const resolveProjectUuid = async () => {
      if (providedProjectUuid) {
        setResolvedProjectUuid(providedProjectUuid)
        return
      }
      
      if (shareId) {
        try {
          const shareInfo = await getShareLinkInfo(shareId)
          if (shareInfo.success && shareInfo.data) {
            const uuid = shareInfo.data.projectUuid || shareInfo.data.project_uuid
            if (uuid) {
              setResolvedProjectUuid(uuid)
              return
            }
          }
        } catch (err) {
          console.error('通过shareId获取projectUuid失败:', err)
        }
      }
      
      if (projectId) {
        const project = data.projects.find(p => p.id === projectId)
        if (project?.uuid) {
          setResolvedProjectUuid(project.uuid)
          return
        }
      }
      
      setResolvedProjectUuid(null)
    }
    
    resolveProjectUuid()
  }, [providedProjectUuid, shareId, projectId, data.projects])

  useEffect(() => {
    if (userUuid && resolvedProjectUuid) {
      loadShareLinks()
    }
  }, [userUuid, resolvedProjectUuid])

  const loadShareLinks = async () => {
    if (!userUuid || !resolvedProjectUuid) return
    
    try {
      setIsLoading(true)
      const response = await getUserShareLinks(userUuid, resolvedProjectUuid)
      if (response.success) {
        const links = response.data || []
        setShareLinks(links)
        
        // 如果提供了 shareId，优先加载该分享链接的设置
        let targetLink = links[0]
        if (shareId && links.length > 0) {
          const foundLink = links.find(link => {
            const linkShareId = link.shareUrl.replace('/share/', '')
            return linkShareId === shareId || link.shareId === shareId
          })
          if (foundLink) {
            targetLink = foundLink
          }
        }
        
        // 如果有已有分享链接，加载其设置
        if (targetLink) {
          setAllowDownload(targetLink.allowDownload !== false)
          setLockInterface(targetLink.lockInterface !== undefined ? targetLink.lockInterface : true)
          setMessage(targetLink.message || '')
          // 如果已有密码，显示占位符（不显示真实密码）
          setPassword(targetLink.hasPassword ? '********' : '')
          
          // 计算过期天数
          if (targetLink.expiresAt) {
            const expiresAt = new Date(targetLink.expiresAt)
            const createdAt = new Date(targetLink.createdAt)
            const daysDiff = Math.ceil((expiresAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24))
            setExpiresInDays(daysDiff > 0 ? daysDiff : '')
          } else {
            setExpiresInDays('')
          }
        }
      }
    } catch (err: any) {
      setError(err.message || '加载分享链接失败')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateShare = async () => {
    if (!userUuid) {
      setError('用户未登录')
      return
    }

    if (!resolvedProjectUuid) {
      setError('项目UUID不存在，无法创建分享链接')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      // 如果提供了 projectId，先推送项目数据（包括设置和所有元素）到服务器，确保所有数据都已同步
      if (projectId) {
        try {
          await pushProjectToServer(projectId)
          // 等待一下，确保服务器数据已更新
          await new Promise(resolve => setTimeout(resolve, 500))
        } catch (pushErr: any) {
          setError('上传画布失败: ' + pushErr.message)
          setIsLoading(false)
          return
        }
      }
      
      // 获取更新后的项目UUID和封面图信息
      let finalProjectUuid = resolvedProjectUuid
      let latestProject = null
      
      if (projectId) {
        const updatedProject = data.projects.find(p => p.id === projectId)
        if (updatedProject?.uuid) {
          finalProjectUuid = updatedProject.uuid
          latestProject = updatedProject
        }
        
        // 从 legacy-script 获取最新的项目数据（包含最新的封面图）
        const win = window as any
        if (typeof win.getCurrentProject === 'function') {
          const currentProject = win.getCurrentProject()
          // 如果当前项目就是我们要分享的项目，使用它的数据
          if (currentProject && (currentProject.id === projectId || currentProject.uuid === finalProjectUuid)) {
            latestProject = currentProject
          }
        }
      }
      
      // 创建或更新分享链接
      // 如果密码是占位符（********），说明用户没有修改密码，传undefined保持原密码
      const passwordToSend = password === '********' ? undefined : (password || undefined)
      
      // 先创建分享链接
      const response = await createShareLink(
        userUuid, 
        finalProjectUuid, 
        expiresInDays ? Number(expiresInDays) : undefined,
        allowDownload,
        message || undefined,
        passwordToSend,
        lockInterface
      )
      
      // 如果创建成功，并且有最新的封面图信息，更新分享链接的封面图
      if (response.success && latestProject) {
        const coverImageFileId = latestProject.coverImage && !latestProject.coverImage.startsWith('data:image/') ? latestProject.coverImage : null
        const coverThumbnailFileId = latestProject.coverThumbnailFileId || null
        
        // 如果有封面图，更新分享链接的封面图字段
        if (coverImageFileId || coverThumbnailFileId) {
          try {
            // 调用后端API更新分享链接的封面图
            await fetch(`/api/share/${response.shareId}/cover`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                coverImageFileId,
                coverThumbnailFileId
              })
            })
          } catch (err) {
            console.warn('更新分享链接封面图失败:', err)
            // 不影响主流程，继续执行
          }
        }
      }
      if (response.success) {
        await loadShareLinks()
        // loadShareLinks 会自动更新按钮文本和表单值
      } else {
        setError(response.message || '保存分享设置失败')
      }
    } catch (err: any) {
      setError(err.message || '创建分享链接失败')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteShare = async (shareId: string) => {
    if (!userUuid) return

    try {
      setIsLoading(true)
      setError(null)
      const response = await deleteShareLink(userUuid, shareId)
      if (response.success) {
        await loadShareLinks()
      } else {
        setError(response.message || '删除分享链接失败')
      }
    } catch (err: any) {
      setError(err.message || '删除分享链接失败')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyLink = (shareUrl: string) => {
    // shareUrl格式是 /share/{shareId}，需要转换为完整URL
    const shareId = shareUrl.replace('/share/', '')
    const fullUrl = `${window.location.origin}/share/${shareId}`
    
    // 立即打开新页面（必须在用户点击事件中同步调用，避免被浏览器阻止）
    window.open(fullUrl, '_blank')
    
    // 设置复制成功状态
    setCopiedShareId(shareId)
    
    // 复制到剪贴板
    navigator.clipboard.writeText(fullUrl).then(() => {
      // 2秒后恢复按钮状态
      setTimeout(() => {
        setCopiedShareId(null)
      }, 2000)
    }).catch(() => {
      // 降级方案
      const textArea = document.createElement('textarea')
      textArea.value = fullUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      
      // 2秒后恢复按钮状态
      setTimeout(() => {
        setCopiedShareId(null)
      }, 2000)
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[1999] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl min-w-[600px] max-w-[800px] w-[90%] max-h-[90vh] overflow-y-auto p-6 z-[2000]">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            分享画布
          </h3>
          <p className="text-sm text-gray-600">
            生成分享链接，其他人可以通过链接查看此画布（只读模式）
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
          </div>
        )}

        {!resolvedProjectUuid && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700">
            项目尚未同步到服务器，请先点击推送按钮同步项目后再创建分享链接
          </div>
        )}

        {/* 分享设置 */}
        <div className="mb-6 space-y-4 border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700">分享设置</h4>
          
          {/* 是否允许下载 */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allowDownload"
              checked={allowDownload}
              onChange={(e) => setAllowDownload(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="allowDownload" className="text-sm text-gray-700 cursor-pointer">
              允许下载文件
            </label>
          </div>
          
          {/* 锁定界面设置 */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="lockInterface"
              checked={lockInterface}
              onChange={(e) => setLockInterface(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="lockInterface" className="text-sm text-gray-700 cursor-pointer">
              锁定界面（锁定后用户无法拖动画布元素）
            </label>
          </div>
          
          {/* 访问密码 */}
          <div>
            <label htmlFor="sharePassword" className="block text-sm font-medium text-gray-700 mb-2">
              🔐 访问密码（留空表示无需密码）
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="sharePassword"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => {
                  // 如果是占位符，清空让用户输入新密码
                  if (password === '********') {
                    setPassword('')
                  }
                }}
                placeholder="设置访问密码..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              设置密码后，访问者需要输入正确密码才能查看内容
            </p>
          </div>
          
          {/* 访问期限 */}
          <div>
            <label htmlFor="expiresInDays" className="block text-sm font-medium text-gray-700 mb-2">
              访问期限（天数，留空表示永久有效）
            </label>
            <input
              type="number"
              id="expiresInDays"
              min="1"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="例如：7（表示7天后过期）"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* 提示信息 */}
          <div>
            <label htmlFor="shareMessage" className="block text-sm font-medium text-gray-700 mb-2">
              提示信息（访问者首次查看时会显示）
            </label>
            <textarea
              id="shareMessage"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="例如：欢迎查看我的画布，请勿外传..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
        </div>

        <div className="mb-6">
          <button
            onClick={handleCreateShare}
            disabled={isLoading || !resolvedProjectUuid}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isLoading ? '处理中...' : shareLinks.length > 0 ? '更新分享设置' : '生成分享链接'}
          </button>
        </div>

        {shareLinks.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">已有分享链接：</h4>
            {shareLinks.map((link) => {
              const shareId = link.shareUrl.replace('/share/', '')
              const fullUrl = `${window.location.origin}/share/${shareId}`
              return (
                <div
                  key={link.shareId}
                  className="p-3 bg-gray-50 border border-gray-200 rounded-md"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      id={`share-input-${link.shareId}`}
                      value={fullUrl}
                      readOnly
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded bg-white min-w-0"
                    />
                    <button
                      onClick={() => handleCopyLink(link.shareUrl)}
                      className={`px-3 py-1 text-sm text-white rounded transition-all duration-300 cursor-pointer flex-shrink-0 ${
                        copiedShareId === shareId
                          ? 'bg-green-600 scale-105 shadow-lg'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {copiedShareId === shareId ? (
                        <span className="flex items-center gap-1">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          已复制
                        </span>
                      ) : (
                        '复制'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        window.open(fullUrl, '_blank')
                      }}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors cursor-pointer flex-shrink-0"
                    >
                      打开
                    </button>
                    <button
                      onClick={async () => {
                        await handleDeleteShare(link.shareId)
                        // 清空输入框
                        const input = document.getElementById(`share-input-${link.shareId}`) as HTMLInputElement
                        if (input) {
                          input.value = ''
                        }
                      }}
                      disabled={isLoading}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
                    >
                      删除
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>
                      创建时间: {new Date(link.createdAt).toLocaleString('zh-CN')}
                      {link.expiresAt && ` | 过期时间: ${new Date(link.expiresAt).toLocaleString('zh-CN')}`}
                    </div>
                    <div>
                      允许下载: {link.allowDownload !== false ? '是' : '否'}
                      {' | '}访问密码: {link.hasPassword ? '已设置' : '无'}
                      {link.message && ` | 提示信息: ${link.message}`}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="flex gap-2 justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShareDialog

